import React from 'react';
import { motion } from 'framer-motion';

const coreExpertise = [
  'Laravel Architecture',
  'API Development',
  'SaaS Platforms',
  'Database Optimization',
  'Queue Systems',
  'Multi-tenant Systems',
];

const groupedSkills = [
  {
    id: 1,
    category: 'Backend',
    skills: [
      'PHP (11 Years)',
      'Laravel (9 Years)',
      'CodeIgniter',
      'WordPress/WooCommerce',
      'REST API Design',
      'Service Layer',
      'Repository Pattern',
      'Event-Driven Architecture',
    ],
  },
  {
    id: 2,
    category: 'Frontend',
    skills: [
      'Vue.js',
      'React.js',
      'Livewire',
      'Alpine.js',
      'JavaScript (ES6+)',
      'Tailwind CSS',
      'Bootstrap',
      'HTML5/CSS3',
    ],
  },
  {
    id: 3,
    category: 'Databases',
    skills: [
      'MySQL Optimization',
      'Redis (Caching & Queues)',
      'PostgreSQL',
      'Database Indexing',
      'Normalization',
      'Laravel Horizon',
    ],
  },
  {
    id: 4,
    category: 'Cloud & DevOps',
    skills: [
      'AWS (S3, EC2, RDS)',
      'DigitalOcean',
      'GitHub Actions (CI/CD)',
      'GitLab CI',
      'Bitbucket Pipelines',
      'LAMP/LEMP Stack',
    ],
  },
  {
    id: 5,
    category: 'Integrations',
    skills: [
      'Stripe',
      'PayPal',
      'Authorize.Net',
      'Razorpay',
      'Google APIs',
      'Facebook API',
      'Twitter API',
      '3rd Party SaaS Integrations',
    ],
  },
];

const categoryStyles = {
  Backend: 'bg-blue-100 text-blue-800 border-blue-200',
  Frontend: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  Databases: 'bg-indigo-100 text-indigo-800 border-indigo-200',
  'Cloud & DevOps': 'bg-amber-100 text-amber-800 border-amber-200',
  Integrations: 'bg-rose-100 text-rose-800 border-rose-200',
};

const Skills = () => {
  return (
    <section id="skills" className="py-24 bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-14"
        >
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 mb-4">
            Technical Skills
          </h2>
          <div className="w-20 h-1 bg-blue-600 mx-auto mb-6"></div>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto leading-relaxed">
            Practical expertise across architecture, backend engineering, and scalable product development.
          </p>
        </motion.div>

        {/* Core Expertise */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="mb-12 bg-gray-50 border border-gray-200 rounded-xl p-6 md:p-8 shadow-md"
        >
          <h3 className="text-xl md:text-2xl font-semibold tracking-tight text-gray-900 mb-5">
            Core Expertise
          </h3>
          <div className="flex flex-wrap gap-3">
            {coreExpertise.map((item) => (
              <span
                key={item}
                className="inline-flex items-center rounded-full bg-blue-600/10 text-blue-800 border border-blue-200 px-4 py-2 text-sm md:text-base font-medium"
              >
                {item}
              </span>
            ))}
          </div>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-10">
          {/* Grouped Skills */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
            className="space-y-6"
          >
            {groupedSkills.map((group, index) => (
              <motion.div
                key={group.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.08 }}
                viewport={{ once: true }}
                className="bg-gray-50 border border-gray-200 rounded-xl p-6 shadow-md hover:-translate-y-2 transition-all duration-300"
              >
                <h4 className="text-lg md:text-xl font-semibold text-gray-900 mb-4 tracking-tight">
                  {group.category}
                </h4>
                <div className="flex flex-wrap gap-2.5">
                  {group.skills.map((skill) => (
                    <span
                      key={skill}
                      className={`inline-flex items-center rounded-full border px-3 py-1.5 text-sm font-medium ${categoryStyles[group.category]}`}
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Skills Image */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="relative"
          >
            <div className="sticky top-24">
              <img
                src="https://images.unsplash.com/photo-1499951360447-b19be8fe80f5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwyfHx3b3Jrc3BhY2V8ZW58MHx8fHwxNzU0MTM4MzE1fDA&ixlib=rb-4.1.0&q=85"
                alt="Development workspace"
                className="rounded-xl shadow-md w-full h-80 object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-blue-900/20 to-transparent rounded-xl"></div>

              <div className="absolute bottom-6 left-6 right-6">
                <div className="bg-white/95 backdrop-blur-sm p-4 rounded-lg">
                  <div className="grid grid-cols-2 gap-4 text-center">
                    <div>
                      <div className="text-2xl font-bold text-blue-600">11</div>
                      <div className="text-sm text-gray-600">Years PHP</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-blue-600">9</div>
                      <div className="text-sm text-gray-600">Years Laravel</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Skills;
